<template>
  <el-container class="layout">
    <el-aside width="240px" class="aside">
      <router-link to="/" class="brand" aria-label="YOJ 首页">
        <span class="brand-mark">&lt;/&gt;</span>
        <span class="brand-copy">
          <strong>YOJ</strong>
          <small>在线评测系统</small>
        </span>
      </router-link>
      <div class="nav-caption">工作空间</div>
      <el-menu router :default-active="route.path" class="menu">
        <el-menu-item v-for="item in menus" :key="item.key" :index="item.key">
          <span class="menu-icon-wrap"><el-icon class="menu-icon"><component :is="item.icon" /></el-icon></span>
          <span>{{ item.label }}</span>
        </el-menu-item>
      </el-menu>
      <div class="aside-note">
        <span class="status-dot"></span>
        <span>评测服务已就绪</span>
      </div>
    </el-aside>
    <el-container class="workspace">
      <el-header class="header" height="76px">
        <div class="page-heading">
          <span class="page-title">{{ currentTitle }}</span>
        </div>
        <div class="header-right">
          <template v-if="isLoggedIn">
            <div class="user-profile">
              <span class="user-avatar">{{ userInitial }}</span>
              <span class="user-name">{{ loginUserStore.loginUser.userName }}</span>
            </div>
            <el-button size="small" @click="handleLogout">退出</el-button>
          </template>
          <template v-else>
            <span class="guest-label">探索算法的世界</span>
            <el-button size="small" type="primary" @click="goToLogin">登录</el-button>
          </template>
        </div>
      </el-header>
      <el-main class="main">
        <router-view />
      </el-main>
      <el-footer class="footer" height="48px">
        <span>YOJ</span>
        <span class="footer-divider">·</span>
        <span>让每一次提交都有价值</span>
      </el-footer>
    </el-container>
  </el-container>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLoginUserStore } from '@/store/loginUser'
import checkAccess from '@/access/checkAccess'
import { HomeFilled, List, Document, Setting } from '@element-plus/icons-vue'

const MENU_ICONS = {
  '/': HomeFilled,
  '/problems': List,
  '/submissions': Document,
  '/manage/problems': Setting,
}

const route = useRoute()
const router = useRouter()
const loginUserStore = useLoginUserStore()

const isLoggedIn = computed(() => {
  return loginUserStore.loginUser.userRole !== 'notLogin'
})

const currentTitle = computed(() => {
  return route.meta?.title || ''
})

const userInitial = computed(() => {
  return (loginUserStore.loginUser.userName || 'U').trim().charAt(0).toUpperCase()
})

const menus = computed(() => {
  // 扁平化：只提取一级菜单项（DefaultLayout 的 children）
  const layoutRoute = router.options.routes.find(r => r.path === '/')
  if (!layoutRoute?.children) return []

  return layoutRoute.children
    .filter((route) => {
      if (route.meta?.hideInMenu) return false
      const requiredAccess = route.meta?.access
      return checkAccess(loginUserStore.loginUser, requiredAccess)
    })
    .map((route) => {
      const key = `/${route.path}`.replace(/\/+/g, '/')
      return {
        key,
        label: route.name || '未命名',
        icon: MENU_ICONS[key] || Document,
      }
    })
})

function goToLogin() {
  router.push('/user/login')
}

async function handleLogout() {
  try {
    const { logout } = await import('@/api/yonghuguanli')
    await logout()
  } catch (e) {
    console.error('退出失败', e)
  }
  loginUserStore.setLoginUserInfo({ userName: '未登录', userRole: 'notLogin' })
  router.push('/')
}
</script>

<style scoped>
.layout {
  min-height: 100vh;
}

.aside {
  position: sticky;
  top: 16px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 32px);
  margin: 16px 0 16px 16px;
  padding: 24px 20px;
  border-radius: 24px;
  border: 1px solid #d6d3d1;
  background: #ffffff;
  box-shadow: 0 18px 36px -18px rgba(120, 113, 108, 0.42), 0 5px 14px -8px rgba(120, 113, 108, 0.32);
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 4px 24px;
  color: var(--yoj-text);
  text-decoration: none;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  color: #c2410c;
  font-family: Consolas, monospace;
  font-size: 17px;
  font-weight: 700;
  border-radius: 50%;
  background: #fff7ed;
  transition: transform 200ms ease-in-out, background-color 200ms ease-in-out;
}

.brand:hover .brand-mark {
  transform: translateY(-2px);
  background: #ffedd5;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-copy strong {
  font-size: 24px;
  line-height: 1.1;
  letter-spacing: 0.08em;
}

.brand-copy small {
  color: var(--yoj-text-muted);
  font-size: 12px;
}

.nav-caption {
  margin: 28px 12px 12px;
  color: var(--yoj-text-muted);
  font-size: 13px;
  font-weight: 500;
}

.menu {
  border-right: none;
  background: transparent;
}

.menu :deep(.el-menu-item) {
  height: 52px;
  margin: 6px 0;
  padding: 0 12px !important;
  color: var(--yoj-text-secondary);
  border-radius: 16px;
  transition: color 200ms ease-in-out, background-color 200ms ease-in-out, transform 200ms ease-in-out;
}

.menu :deep(.el-menu-item:hover) {
  color: #9a3412;
  background: #fafaf9;
  transform: translateY(-1px);
}

.menu :deep(.el-menu-item.is-active) {
  color: #9a3412;
  font-weight: 600;
  background: #fff7ed;
}

.menu-icon-wrap {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-right: 12px;
  border-radius: 50%;
  background: #f5f5f4;
  transition: background-color 200ms ease-in-out;
}

.is-active .menu-icon-wrap {
  background: #ffedd5;
}

.menu-icon {
  font-size: 17px;
}

.aside-note {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 18px 12px 0;
  color: var(--yoj-text-muted);
  font-size: 12px;
}

.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 5px #ecfdf5;
}

.workspace {
  min-width: 0;
}

.header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 28px;
  padding: 0 4px;
  border-bottom: 1px solid #d6d3d1;
  background: var(--yoj-bg);
}

.page-title {
  color: var(--yoj-text);
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.header-right,
.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-profile {
  gap: 10px;
  padding: 6px 14px 6px 6px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(168, 162, 158, 0.6);
}

.user-avatar {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  color: #9a3412;
  font-size: 14px;
  font-weight: 600;
  border-radius: 50%;
  background: #fff7ed;
}

.user-name,
.guest-label {
  max-width: 180px;
  overflow: hidden;
  color: var(--yoj-text-secondary);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.main {
  height: calc(100vh - 124px);
  padding: 28px;
  overflow: auto;
  background: transparent;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--yoj-text-muted);
  font-size: 12px;
}

.footer > span:first-child {
  color: var(--yoj-accent-strong);
  font-weight: 600;
  letter-spacing: 0.08em;
}

.footer-divider {
  color: #a8a29e;
}

@media (max-width: 760px) {
  .layout {
    display: block;
  }

  .aside {
    position: relative;
    top: 0;
    width: auto !important;
    height: auto;
    margin: 12px 12px 0;
    padding: 18px;
  }

  .brand {
    padding: 0 0 18px;
  }

  .brand-mark {
    width: 42px;
    height: 42px;
  }

  .brand-copy strong {
    font-size: 21px;
  }

  .nav-caption,
  .aside-note {
    display: none;
  }

  .menu {
    display: flex;
    gap: 8px;
    padding-top: 14px;
    overflow-x: auto;
  }

  .menu :deep(.el-menu-item) {
    flex: 0 0 auto;
    height: 48px;
    margin: 0;
    padding: 0 12px !important;
  }

  .menu-icon-wrap {
    width: 30px;
    height: 30px;
    margin-right: 8px;
  }

  .header {
    position: relative;
    min-height: 76px;
    margin: 0 20px;
  }

  .guest-label {
    display: none;
  }

  .main {
    height: auto;
    min-height: calc(100vh - 280px);
    padding: 24px 16px;
    overflow: visible;
  }
}

@media (max-width: 480px) {
  .header {
    margin: 0 16px;
  }

  .page-title {
    font-size: 19px;
  }

  .user-profile {
    padding-right: 6px;
  }

  .user-name {
    display: none;
  }

  .header-right {
    gap: 8px;
  }

  .main {
    padding: 20px 12px;
  }
}
</style>
